import { useState, useEffect, useRef } from "react";
import { 
  ref, 
  onValue, 
  off, 
  push, 
  set, 
  update, 
  serverTimestamp,
  onChildAdded,
  query,
  limitToLast
} from "firebase/database";
import { rtdb } from "@/lib/firebase";
import { 
  Search, 
  MessageSquare, 
  User, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Send,
  Loader2,
  ChevronRight,
  Bell,
  Check,
  CheckCheck,
  PlusCircle,
  MessageSquarePlus,
  Headset,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getAllUsers } from "@/services/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [lastAdminMessageId, setLastAdminMessageId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useRef(null);
  const [notification, setNotification] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const initialLoadRef = useRef(true);

  // Manual Complaint State
  const [isComplaintDialogOpen, setIsComplaintDialogOpen] = useState(false);
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [complaintData, setComplaintData] = useState({
    userId: "",
    subject: "",
    text: "",
    userName: ""
  });
  const [ticketFilter, setTicketFilter] = useState("all"); // all, app, manual

  const COMPLAINT_SUBJECTS = [
    "Payment Issue",
    "Waste Issue",
    "Captain Issue",
    "Account Issue",
    "Other"
  ];

  // Fetch users for manual complaint recording
  useEffect(() => {
    if (isComplaintDialogOpen && allUsers.length === 0) {
      const fetchUsers = async () => {
        try {
          const data = await getAllUsers('user');
          if (data && data.success) {
            setAllUsers(data.users.map(u => ({
              id: u._id || u.id,
              name: u.name,
              contact: u.contact || u.phone
            })));
          }
        } catch (error) {
          console.error("Error fetching users:", error);
          toast.error("Failed to load users");
        }
      };
      fetchUsers();
    }
  }, [isComplaintDialogOpen, allUsers.length]);

  const handleRecordComplaint = async (e) => {
    e.preventDefault();
    if (!complaintData.userId || !complaintData.subject || !complaintData.text) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmittingComplaint(true);
    const ticketsRef = ref(rtdb, "tickets");
    const newTicketRef = push(ticketsRef);
    const ticketId = newTicketRef.key;

    const newTicket = {
      subject: `[MANUAL] ${complaintData.subject}`,
      userName: complaintData.userName,
      userId: complaintData.userId,
      status: "open",
      createdAt: serverTimestamp(),
      source: "manual",
    };

    try {
      await set(newTicketRef, newTicket);
      
      const firstMessageRef = push(ref(rtdb, `tickets/${ticketId}/messages`));
      await set(firstMessageRef, {
        text: complaintData.text,
        sender: "user", // Recording as user message
        createdAt: serverTimestamp(),
        isRead: true,
      });

      toast.success("Complaint recorded successfully");
      setIsComplaintDialogOpen(false);
      setComplaintData({ userId: "", subject: "", text: "", userName: "" });
      
      // Select the new ticket
      setSelectedTicket({ id: ticketId, ...newTicket });
    } catch (error) {
      console.error("Error recording complaint:", error);
      toast.error("Failed to record complaint");
    } finally {
      setIsSubmittingComplaint(false);
    }
  };

  const filteredUsers = allUsers.filter(user => 
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.contact?.toLowerCase().includes(userSearchTerm.toLowerCase())
  ).slice(0, 5);

  // Use a ref to always have the latest selected ticket ID for the listener
  const selectedTicketIdRef = useRef(null);
  useEffect(() => {
    selectedTicketIdRef.current = selectedTicket?.id;
  }, [selectedTicket]);

  // Global listener for NEW messages in ALL tickets
  useEffect(() => {
    const ticketsRef = ref(rtdb, "tickets");
    const listeners = [];

    onValue(ticketsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
            Object.keys(data).forEach(tId => {
                const msgRef = query(ref(rtdb, `tickets/${tId}/messages`), limitToLast(1));
                const unsub = onChildAdded(msgRef, (msgSnapshot) => {
                    if (initialLoadRef.current) return;

                    const msg = msgSnapshot.val();
                    if (msg.sender === 'user' && selectedTicketIdRef.current !== tId) {
                        const ticketInfo = data[tId];
                        setNotification({
                            ticketId: tId,
                            subject: ticketInfo.subject,
                            userName: ticketInfo.userName,
                            text: msg.text
                        });
                        setIsDialogOpen(true);
                    }
                });
                listeners.push({ ref: msgRef, unsub });
            });
        }
        
        setTimeout(() => {
            initialLoadRef.current = false;
        }, 2000);
    }, { onlyOnce: true });

    return () => {
        listeners.forEach(l => off(l.ref));
    };
  }, []);

  // Fetch all tickets list
  useEffect(() => {
    const ticketsRef = ref(rtdb, "tickets");
    const unsubscribe = onValue(ticketsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ticketList = Object.keys(data).map((key) => {
            const ticket = data[key];
            const messages = ticket.messages || {};
            const unreadCount = Object.values(messages).filter(m => m.sender === 'user' && m.isRead === false).length;
            
            return {
                id: key,
                ...ticket,
                unreadCount
            };
        }).sort((a, b) => b.createdAt - a.createdAt);
        setTickets(ticketList);
      } else {
        setTickets([]);
      }
      setIsLoading(false);
    });

    return () => off(ticketsRef);
  }, []);

  // Fetch messages for selected ticket
  useEffect(() => {
    if (!selectedTicket) {
      setMessages([]);
      return;
    }

    const messagesRef = ref(rtdb, `tickets/${selectedTicket.id}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const updates = {};
        let lastAdminId = null;
        const messageList = Object.keys(data).map((key) => {
          const msg = data[key];
          // Mark user messages as read if admin is viewing
          if (msg.sender === 'user' && msg.isRead === false) {
            updates[`tickets/${selectedTicket.id}/messages/${key}/isRead`] = true;
          }
          if (msg.sender === 'admin') {
            lastAdminId = key;
          }
          return {
            id: key,
            ...msg,
          };
        }).sort((a, b) => a.createdAt - b.createdAt); // Keep up-to-down sorting
        
        setMessages(messageList);
        setLastAdminMessageId(lastAdminId);

        if (Object.keys(updates).length > 0) {
          update(ref(rtdb), updates);
        }
      } else {
        setMessages([]);
      }
    });

    return () => off(messagesRef);
  }, [selectedTicket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
        if (scrollRef.current) {
            const scrollElement = scrollRef.current.getScrollElement();
            scrollElement.scrollTo({
                top: scrollElement.scrollHeight,
                behavior: "smooth"
            });
        }
    };

    // Use a small timeout to ensure DOM is updated
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket || selectedTicket.status === 'closed') return;

    const messagesRef = ref(rtdb, `tickets/${selectedTicket.id}/messages`);
    const newMessageRef = push(messagesRef);

    try {
      await set(newMessageRef, {
        text: newMessage.trim(),
        sender: "admin",
        createdAt: serverTimestamp(),
        isRead: false,
      });
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const toggleTicketStatus = async (ticketId, currentStatus) => {
    const newStatus = currentStatus === "open" ? "closed" : "open";
    const ticketRef = ref(rtdb, `tickets/${ticketId}`);
    
    try {
      await update(ticketRef, { status: newStatus });
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
    }
  };

  const handleViewMessage = () => {
    if (notification) {
        const targetTicket = tickets.find(t => t.id === notification.ticketId);
        if (targetTicket) {
            setSelectedTicket(targetTicket);
        }
        setIsDialogOpen(false);
        setNotification(null);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = (ticket.subject || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ticket.userName || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = ticketFilter === "all" || 
                         (ticketFilter === "app" && ticket.source !== "manual") ||
                         (ticketFilter === "manual" && ticket.source === "manual");
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="flex h-[calc(100vh-140px)] gap-6">
      {/* Record Manual Complaint Dialog */}
      <Dialog open={isComplaintDialogOpen} onOpenChange={setIsComplaintDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-primary" />
              Record Customer Complaint
            </DialogTitle>
            <DialogDescription>
              Record a complaint or feedback received from a customer via support call.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRecordComplaint} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User *</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search user by name or phone..."
                  className="pl-9"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                />
              </div>
              
              {userSearchTerm && filteredUsers.length > 0 && (
                <div className="mt-2 border rounded-md overflow-hidden bg-card shadow-sm">
                  {filteredUsers.map(user => (
                    <button
                      key={user.id}
                      type="button"
                      className={cn(
                        "w-full text-left p-2 text-sm hover:bg-primary/5 transition-colors border-b last:border-0 flex justify-between items-center",
                        complaintData.userId === user.id ? "bg-primary/10 border-primary/20" : ""
                      )}
                      onClick={() => {
                        setComplaintData(prev => ({ ...prev, userId: user.id, userName: user.name }));
                        setUserSearchTerm("");
                      }}
                    >
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.contact}</p>
                      </div>
                      {complaintData.userId === user.id && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  ))}
                </div>
              )}
              
              {complaintData.userId && (
                <div className="mt-2 p-2 bg-primary/5 rounded-md border border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{complaintData.userName}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 text-xs" 
                    onClick={() => setComplaintData(prev => ({ ...prev, userId: "", userName: "" }))}
                  >
                    Change
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Select 
                value={complaintData.subject} 
                onValueChange={(val) => setComplaintData(prev => ({ ...prev, subject: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a subject" />
                </SelectTrigger>
                <SelectContent>
                  {COMPLAINT_SUBJECTS.map((sub) => (
                    <SelectItem key={sub} value={sub}>
                      {sub}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="complaint">Complaint Details *</Label>
              <Textarea
                id="complaint"
                placeholder="Describe the complaint in detail..."
                className="min-h-[120px]"
                required
                value={complaintData.text}
                onChange={(e) => setComplaintData(prev => ({ ...prev, text: e.target.value }))}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsComplaintDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingComplaint}>
                {isSubmittingComplaint ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  "Record Complaint"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Message Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] border-primary/20 shadow-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-primary animate-bounce" />
                </div>
                <DialogTitle className="text-primary">New Message Received</DialogTitle>
            </div>
            <DialogDescription className="text-foreground font-medium">
              {notification?.userName} sent a message regarding:
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted/50 p-4 rounded-xl border border-border mt-2">
            <p className="text-xs font-bold text-muted-foreground uppercase mb-1">{notification?.subject}</p>
            <p className="text-sm italic">"{notification?.text}"</p>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Dismiss</Button>
            <Button onClick={handleViewMessage} className="bg-primary hover:bg-primary-dark shadow-md">
                See Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tickets List */}
      <Card className="w-80 flex flex-col shadow-sm border-border overflow-hidden">
        <CardHeader className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Tickets
            </CardTitle>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
              onClick={() => setIsComplaintDialogOpen(true)}
              title="Record Manual Complaint"
            >
              <PlusCircle className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex gap-1 mb-2">
            <Button 
              size="sm" 
              variant={ticketFilter === "all" ? "default" : "outline"} 
              className="flex-1 h-7 text-[10px] px-1"
              onClick={() => setTicketFilter("all")}
            >
              All
            </Button>
            <Button 
              size="sm" 
              variant={ticketFilter === "app" ? "default" : "outline"} 
              className="flex-1 h-7 text-[10px] px-1"
              onClick={() => setTicketFilter("app")}
            >
              App
            </Button>
            <Button 
              size="sm" 
              variant={ticketFilter === "manual" ? "default" : "outline"} 
              className="flex-1 h-7 text-[10px] px-1"
              onClick={() => setTicketFilter("manual")}
            >
              Manual
            </Button>
          </div>

          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              className="pl-9 bg-muted/50 border-none h-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-2">
            {isLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground p-8">No tickets found</p>
            ) : (
              filteredTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={cn(
                    "w-full text-left p-3 rounded-xl transition-smooth group relative border border-transparent",
                    selectedTicket?.id === ticket.id 
                      ? "bg-primary text-primary-foreground shadow-md" 
                      : "hover:bg-primary/5 hover:border-primary/20 bg-card"
                  )}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-semibold pr-2 line-clamp-1 flex-1">
                      {ticket.subject}
                    </span>
                    <div className="flex items-center gap-2">
                        {ticket.unreadCount > 0 && (
                            <div className={cn(
                                "min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm",
                                selectedTicket?.id === ticket.id ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
                            )}>
                                {ticket.unreadCount}
                            </div>
                        )}
                        <Badge 
                        variant={ticket.status === 'open' ? 'default' : 'secondary'}
                        className={cn(
                            "text-[10px] px-1.5 py-0",
                            ticket.status === 'open' 
                            ? (selectedTicket?.id === ticket.id ? "bg-primary-foreground text-primary" : "bg-green-500/10 text-green-600 border-none")
                            : "opacity-70"
                        )}
                        >
                        {ticket.status}
                        </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs opacity-80">
                      <User className="w-3 h-3" />
                      <span className="truncate">{ticket.userName || 'User'}</span>
                    </div>
                    {ticket.source === "manual" && (
                      <Badge 
                        variant="default" 
                        className={cn(
                          "text-[8px] h-4 px-1.5 bg-orange-500 hover:bg-orange-600 border-none flex gap-0.5 items-center",
                          ticket.status === 'open' && "animate-pulse"
                        )}
                      >
                        <Headset className="w-2 h-2" />
                        MANUAL
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] opacity-60 mt-1">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                  </div>
                  {selectedTicket?.id !== ticket.id && (
                    <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-smooth" />
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col shadow-sm border-border overflow-hidden">
        {selectedTicket ? (
          <>
            <CardHeader className={cn(
              "p-4 border-b flex flex-row items-center justify-between",
              selectedTicket.source === "manual" ? "bg-orange-50/50" : "bg-card"
            )}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar className="w-10 h-10 border-2 border-primary/20">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {selectedTicket.userName ? selectedTicket.userName[0] : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {selectedTicket.source === "manual" && (
                    <div className="absolute -top-1 -right-1 bg-orange-500 rounded-full p-0.5 border-2 border-white shadow-sm">
                      <Headset className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base font-bold">{selectedTicket.userName || 'User'}</CardTitle>
                    {selectedTicket.source === "manual" && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-orange-200 text-orange-600 bg-orange-50">
                        Manual Complaint
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                    <span className={cn(
                      "w-1.5 h-1.5 rounded-full animate-pulse",
                      selectedTicket.source === "manual" ? "bg-orange-500" : "bg-primary"
                    )} />
                    {selectedTicket.subject}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedTicket.source !== 'manual' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleTicketStatus(selectedTicket.id, selectedTicket.status)}
                    className={cn(
                      "transition-smooth",
                      selectedTicket.status === "open" 
                        ? "hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                        : "hover:bg-green-500/10 hover:text-green-600 text-muted-foreground"
                    )}
                  >
                    {selectedTicket.status === "open" ? (
                      <><XCircle className="w-4 h-4 mr-2" /> Close Ticket</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" /> Reopen Ticket</>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            
            <ScrollArea className="flex-1 p-4 bg-muted/30" viewportRef={scrollRef}>
              <div className="space-y-4">
                {selectedTicket.source === "manual" && (
                  <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 mb-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-orange-800">Manual Complaint Record</p>
                      <p className="text-xs text-orange-700 leading-relaxed">
                        This complaint was recorded manually by a support admin. Actions taken and responses should be recorded here for tracking.
                      </p>
                    </div>
                  </div>
                )}
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <MessageSquare className="w-8 h-8 mb-2 opacity-20" />
                    <p className="text-sm">No messages yet. Send a message to start the conversation.</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className={cn(
                          "flex w-full",
                          msg.sender === "admin" ? "justify-end" : "justify-start"
                        )}
                      >
                      <div className={cn("flex flex-col max-w-[70%]", msg.sender === "admin" ? "items-end" : "items-start")}>
                        <div
                            className={cn(
                            "p-3 rounded-2xl text-sm shadow-sm transition-smooth",
                            msg.sender === "admin"
                                ? (selectedTicket.source === "manual" ? "bg-orange-100 text-orange-900 border border-orange-200" : "bg-primary text-primary-foreground rounded-tr-none")
                                : "bg-card border border-border rounded-tl-none"
                            )}
                        >
                            {selectedTicket.source === "manual" && msg.sender === "admin" && (
                              <p className="text-[10px] font-bold text-orange-600 uppercase mb-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Action Note
                              </p>
                            )}
                            <p className="leading-relaxed">{msg.text}</p>
                        </div>
                        <div className={cn(
                          "flex items-center gap-1 mt-1.5 px-1 opacity-60",
                          msg.sender === "admin" ? "flex-row-reverse" : "flex-row"
                        )}>
                          <span className="text-[11px] font-medium">
                            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {msg.sender === "admin" && msg.id === lastAdminMessageId && (
                            <span className="text-[11px] font-bold italic">
                                • {msg.isRead ? 'seen' : 'delivered'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            <div className="p-4 border-t bg-card">
              {selectedTicket.source === 'manual' ? (
                <div className="flex flex-col gap-2">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder={selectedTicket.status === 'open' ? "Add an internal action note..." : "Ticket is closed"}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={selectedTicket.status === 'closed'}
                      className="flex-1 bg-muted/50 border-none focus-visible:ring-primary"
                    />
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || selectedTicket.status === 'closed'}
                      className="px-5 shadow-md hover:scale-105 transition-smooth active:scale-95 bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </form>
                  {selectedTicket.status === 'open' && (
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => toggleTicketStatus(selectedTicket.id, selectedTicket.status)}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Mark Resolved & Close Complaint
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder={selectedTicket.status === 'open' ? "Type a response..." : "Ticket is closed"}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={selectedTicket.status === 'closed'}
                      className="flex-1 bg-muted/50 border-none focus-visible:ring-primary"
                    />
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || selectedTicket.status === 'closed'}
                      className="px-5 shadow-md hover:scale-105 transition-smooth active:scale-95"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </Button>
                  </form>
                </>
              )}
              {selectedTicket.status === 'closed' && (
                <p className="text-[10px] text-center text-muted-foreground mt-2 font-medium">
                  This {selectedTicket.source === 'manual' ? 'complaint' : 'ticket'} is closed. {selectedTicket.source !== 'manual' && 'Reopen it to send more messages.'}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex-col flex items-center justify-center text-muted-foreground bg-muted/10 p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/5 flex items-center justify-center mb-4">
                <MessageSquare className="w-10 h-10 text-primary opacity-20" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-1">Select a ticket to view conversation</h3>
            <p className="text-sm max-w-xs">Choose a support request from the list on the left to start responding to users.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Tickets;
